import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuthStore } from "../lib/store";
import { authAPI, setLoginTime } from "../lib/api";
import { prefetchAllData } from "../lib/prefetch";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Zap, Loader2 } from "lucide-react";

// Google icon SVG component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export default function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Debug: Log attempt number
    const attemptId = Date.now();
    console.log(`[LOGIN ${attemptId}] Starting login attempt`);
    console.log(`[LOGIN ${attemptId}] Email: ${formData.email}`);

    try {
      console.log(`[LOGIN ${attemptId}] Calling authAPI.login...`);
      const response = await authAPI.login(formData);
      console.log(`[LOGIN ${attemptId}] Response received:`, response.status, response.data?.user?.email);
      
      const { access_token, user } = response.data;
      console.log(`[LOGIN ${attemptId}] Token received: ${access_token ? 'YES' : 'NO'}`);
      
      // Mark login time to prevent 401 race condition logout
      setLoginTime();
      
      setAuth(user, access_token);
      console.log(`[LOGIN ${attemptId}] Auth set`);
      
      // Start prefetching trends and sounds in the background
      // This runs in parallel while the user sees the success toast
      prefetchAllData().catch(err => console.warn('[LOGIN] Prefetch failed:', err));
      
      // Verify token was saved before navigating
      const savedToken = localStorage.getItem('auth_token');
      console.log(`[LOGIN ${attemptId}] Token in localStorage after setAuth: ${savedToken ? 'YES' : 'NO'}`);
      
      toast.success("Welcome back!");
      
      // Small delay to ensure state propagation before navigation
      console.log(`[LOGIN ${attemptId}] Waiting 100ms before navigation...`);
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log(`[LOGIN ${attemptId}] Navigating to dashboard`);
      navigate("/dashboard");
    } catch (error) {
      console.error(`[LOGIN ${attemptId}] FAILED:`, {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        code: error.code
      });
      const errorMessage = error.response?.data?.detail 
        || error.message 
        || "Login failed. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      console.log(`[LOGIN ${attemptId}] Complete`);
    }
  };

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Use window.location.origin directly for OAuth redirect
    const timestamp = Date.now();
    const redirectUrl = `${window.location.origin}?_t=${timestamp}`;
    console.log('[GOOGLE_LOGIN] Redirect URL:', redirectUrl);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#0A0A0B]">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#00F0FF]/10 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-[#FF0055]/10 rounded-full blur-[128px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00F0FF] to-[#FF0055] flex items-center justify-center">
            <Zap className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-2xl text-white tracking-tight">iGoViral</h1>
            <p className="text-xs text-muted-foreground">Content Creator</p>
          </div>
        </div>

        <Card className="bg-card/50 border-border/50 backdrop-blur-xl">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl font-bold text-white">Welcome Back</CardTitle>
            <CardDescription>Sign in to your account to continue</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Google Sign In */}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              className="w-full h-12 border-border/50 bg-white hover:bg-gray-100 text-gray-900 font-medium rounded-xl gap-3 mb-6"
              data-testid="google-login-btn"
            >
              {googleLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <GoogleIcon />
              )}
              Continue with Google
            </Button>

            <div className="relative mb-6">
              <Separator className="bg-border/50" />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground">
                or continue with email
              </span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="creator@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-12 bg-secondary/50 border-border/50 focus:border-[#00F0FF]"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-white">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="h-12 bg-secondary/50 border-border/50 focus:border-[#00F0FF]"
                  data-testid="login-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-[#0A0A0B] font-bold rounded-xl neon-glow"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-6">
              Don't have an account?{" "}
              <Link to="/register" className="text-[#00F0FF] hover:underline font-medium">
                Create one
              </Link>
            </p>
            
            <div className="text-center text-xs text-muted-foreground mt-4 pt-4 border-t border-border/30">
              By continuing, you agree to our{" "}
              <Link to="/terms" className="text-[#00F0FF] hover:underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-[#00F0FF] hover:underline">
                Privacy Policy
              </Link>
            </div>
          </CardContent>
        </Card>
        
        {/* Copyright */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          © {new Date().getFullYear()} iGoViral Content Creator. All rights reserved.
        </p>
      </motion.div>
    </div>
  );
}
